import React from "react";
import Image from "next/image";
import { withCDN, optimizedImage } from "@/src/lib/utils";

export default function ProgramSkeleton() {
  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        .shimmer-dark {
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.05) 0%,
            rgba(255, 255, 255, 0.15) 50%,
            rgba(255, 255, 255, 0.05) 100%
          );
          background-size: 1000px 100%;
          animation: shimmer 2s infinite;
        }
      `}</style>

      <section
        className="
          relative w-full z-30 bg-transparent
          overflow-x-hidden overflow-y-visible
          mt-[-130px] md:mt-[-230px] xl:mt-[-300px] lg:mt-[-270px] 2xl:mt-[-240px]
          min-h-[1000px] md:min-h-[1100px] lg:min-h-[1360px]
          px-4 md:px-10 lg:px-18 xl:px-34
        "
      >
        {/* Background Images - lazy. These used to be preloaded on the
            theory that it warms the cache for the real component, but all
            three variants were fetched while only one is ever shown. */}
        <div className="absolute inset-0 w-full h-full -z-10 hidden lg:block">
          <Image
            src="https://yefranchisees.b-cdn.net/frontend/programs-around-you.svg"
            alt="Programs Around You background"
            fill
            className="object-cover object-top"
            loading="lazy"
          unoptimized
          />
        </div>
        <div className="absolute inset-0 w-full h-full -z-10 hidden md:block lg:hidden">
          <Image
            src="https://yefranchisees.b-cdn.net/frontend/programsAroundYouTab.svg"
            alt="Programs Around You background (tablet)"
            fill
            className="object-cover object-top"
            loading="lazy"
          unoptimized
          />
        </div>
        <div className="absolute inset-0 w-full h-full -z-10 md:hidden">
          <Image
            src="https://yefranchisees.b-cdn.net/frontend/programsAroundYouMobile.svg"
            alt="Programs Around You background (mobile)"
            fill
            className="object-cover object-top"
            loading="lazy"
          unoptimized
          />
        </div>

        {/* Robot Image */}
        <div
          className="
            absolute top-[0rem] md:top-[-1rem] lg:top-[0rem] xl:top-[-1rem] 
            right-[-4rem] sm:right-0 md:right-[-9rem] lg:right-[-9rem] xl:right-[-4rem]
            block z-20 overflow-hidden
          "
        >
          <img
            src={optimizedImage(withCDN("/crawler-crane.png"), 640)}
            alt="Robot"
            className="object-contain w-[20rem] sm:w-[36rem] md:w-[39rem] lg:w-[45rem] xl:w-[50rem] h-auto"
          />
        </div>

        {/* Main Content Skeleton */}
        <div className="relative max-w-[1500px] z-21">
          {/* Header */}
          <div className="mb-[44px] md:mb-0">
            <div className="text-left mb-15 sm:mb-12 md:mb-16 max-w-2xl mt-30 lg:mt-28 xl:mt-30 ">
              <h1 className="uppercase font-bold text-white leading-none text-[40px] md:text-[50px] xl:text-[80px] 2xl:text-[80px] lg:text-[80px] md:w-[60%] lg:w-full">
                PROGRAMS
                <br />
                AROUND YOU
              </h1>
            </div>

            {/* Age Slider Skeleton */}
            <div className="w-full max-w-2xl pt-10 mb-4 sm:mb-14 md:mb-10 lg:mb-5 flex flex-col items-left">
              <div className="relative w-[100%] sm:w-[50%] md:w-[44%] lg:w-[90%] flex flex-col">
                <div className="h-2.5 bg-white/20 rounded-full w-full shimmer-dark" />
                <div className="flex justify-between w-full text-white mt-4 sm:mt-5 md:mt-6 text-[20px] sm:text-base md:text-[20px] xl:text-[26px]">
                  <span>4</span>
                  <span className="text-center text-[20px] sm:text-sm md:text-[20px] xl:text-[26px]">
                    Choose your child&apos;s age
                  </span>
                  <span>18</span>
                </div>
              </div>
            </div>
          </div>

          {/* Program Cards SKELETON */}
          <div className="mt-10 sm:mt-34 md:mt-0 lg:mt-10 xl:mt-10 flex gap-4 sm:gap-6 md:gap-8 overflow-hidden no-scrollbar pt-20 lg:pt-20">
            {[1, 2, 3, 4].map((index) => (
              <div key={index} className="flex-none">
                {/* Reduced height from 420 to 240 */}
                <div
                  className="rounded-2xl overflow-hidden bg-white/10 flex flex-col backdrop-blur-sm border border-white/10 shadow-lg"
                  style={{ width: 320, height: 240 }}
                >
                  {/* Top Image area */}
                  <div className="w-full h-[150px] shimmer-dark" />
                  {/* Text Details Area */}
                  <div className="p-4 flex flex-col gap-3 grow">
                    <div className="shimmer-dark h-5 w-3/4 rounded-md" />
                    {/* Footer / Buttons area */}
                    <div className="mt-auto flex justify-between items-center pt-1">
                       <div className="shimmer-dark h-8 w-24 rounded-full" />
                       <div className="shimmer-dark h-6 w-6 rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button Skeleton */}
          <div className="flex justify-center mt-12 md:mt-24">
            <div className="shimmer-dark h-[43px] md:h-[53px] lg:h-[53px] w-[250px] md:w-[320px] rounded-full" />
          </div>
        </div>
      </section>
    </>
  );
}